import { format, getISODay } from 'date-fns'
import { Building2, CalendarCog, Clock, FolderInput, Lock, Tag } from 'lucide-react'
import { useRef, type ReactNode, type TouchEvent } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { formatHmm } from '@/lib/hours'
import {
  usePickers,
  useSaveCell,
  useWeek,
  useWeekTotals,
  useWorkdayExceptions,
  type CellValues,
} from '@/lib/time-queries'
import { readDescription } from '@/lib/timesheet-copy'
import type { Me, PickerData, WeekCell, WeekRow } from '@/lib/types'
import { cn } from '@/lib/utils'
import { dayStatus, isoWeekId, weekMetaFromId } from '@/lib/week'
import { AddRowButton } from './AddRowButton'
import { CellPopover } from './CellPopover'
import { DayHeaderPopover } from './DayHeaderPopover'
import { FreeDayBanner } from './FreeDayBanner'
import { Legend } from './Legend'

import { RowActions } from './RowActions'
import { RowLabel } from './RowLabel'
import { RowTargetMenu } from './RowTargetMenu'
import { StoplichtDot } from './StoplichtDot'
import { TimesheetToolbar } from './TimesheetToolbar'
import { UtilizationStrip } from './UtilizationStrip'
import { WeekApprovalControl } from './WeekApprovalControl'
import { WeekNav, WeekTotalsStrip } from './WeekNav'
import { closedOn, rowBoundary, rowMenuState, rowsForDay, selectionFromRow, type NewRowSelection } from './week-rows'
import type { TimesheetRows } from './useTimesheetRows'

interface DayListProps {
  weekId: string
  dayIndex: number
  /**
   * Week en dag in een keer, net als bij DayView: "Vandaag" verandert ze allebei, en
   * wie ze los meldt rekent de tweede stap nog met de week van de eerste.
   */
  onNavigate: (weekId: string, dayIndex: number) => void
  personId?: string
  me?: Me
  editable: boolean
  sheet: TimesheetRows
}

export function DayList({
  weekId,
  dayIndex,
  onNavigate,
  personId,
  me,
  editable,
  sheet,
}: DayListProps) {
  const meta = weekMetaFromId(weekId)
  const weekQuery = useWeek(weekId, personId)
  const weekTotalsQuery = useWeekTotals(weekId, personId)
  const pickersQuery = usePickers(personId)
  const saveCell = useSaveCell(personId, me)
  const exceptionsQuery = useWorkdayExceptions(meta.days[0].iso, meta.days[6].iso, personId, editable)

  const todayIso = format(new Date(), 'yyyy-MM-dd')

  const day = meta.days[dayIndex]
  const week = weekQuery.data
  // Een afgesloten week is een slot: de server weigert mutaties tot de week is heropend.
  const weekClosed = week?.approval?.status === 'APPROVED'
  const dayIsClosed = closedOn(week?.approval, meta.days[dayIndex].iso)
  /**
   * Tijdens een wissel van week of persoon houdt react-query de vorige uitslag in
   * beeld. Die dagtotalen en dat akkoord horen bij een andere week: typen zou op de
   * verkeerde regel landen en afsluiten zou de verkeerde week beoordelen.
   */
  const stale = weekQuery.isPlaceholderData
  const canEdit = editable && !weekClosed && !dayIsClosed && !stale
  const dayInfo = week?.dailyTotals.find((d) => d.dayIndex === dayIndex)
  const dayTotal = toNumber(dayInfo?.total)
  const freeDay = dayInfo?.dayType === 'vacation' || dayInfo?.dayType === 'holiday'
  const exception = (exceptionsQuery.data ?? []).find((e) => e.date === day.iso) ?? null

  const { rows: weekRows, localKeysForDay, savedRowKeys, landedRowKey, addRow, dropRow, replaceRow, alignRow, markRowSaved } = sheet

  // Zelfde regel als in de dagweergave: alleen wat op deze dag geboekt is, plus wat je nu
  // toevoegt. De rest staat als chip in de toevoegzone.
  const localKeys = localKeysForDay(dayIndex)
  const rows = rowsForDay(weekRows, localKeys, dayIndex)

  // Een regel die je hier neerzet hoort bij deze dag, net als in de dagweergave.
  const addRowOnDay = (selection: NewRowSelection) => addRow(selection, dayIndex)

  const emptyWorkdays = (week?.dailyTotals ?? [])
    .filter((d) => d.expectedWorkday && toNumber(d.total) === 0)
    .map((d) => `${meta.days[d.dayIndex].short.toLowerCase()} ${meta.days[d.dayIndex].date.getDate()}`)

  function step(delta: number) {
    onNavigate(weekId, Math.min(6, Math.max(0, dayIndex + delta)))
  }

  function goToToday() {
    const today = new Date()
    onNavigate(isoWeekId(today), (getISODay(today) - 1 + 7) % 7)
  }

  const touchStart = useRef<number | null>(null)

  function onTouchStart(event: TouchEvent) {
    touchStart.current = event.touches[0].clientX
  }

  function onTouchEnd(event: TouchEvent) {
    if (touchStart.current === null) return
    const delta = event.changedTouches[0].clientX - touchStart.current
    if (Math.abs(delta) > 50) step(delta < 0 ? 1 : -1)
    touchStart.current = null
  }

  function saveCellValues(row: WeekRow, values: CellValues) {
    const cell = row.cells[dayIndex]
    // De server zet een nieuwe boeking op de eerstvolgende plek van deze dag; een verse
    // regel die een ander slot raadde hoort daar eerst naartoe.
    const rowKey = cell.entryId === null ? alignRow(row.rowKey, dayIndex) : row.rowKey
    // Vanaf nu bestaat er invoer voor deze regel; een rijwissel moet naar de server.
    markRowSaved(rowKey)
    saveCell.mutate({
      rowKey,
      dayIndex,
      date: cell.date,
      projectId: row.projectId,
      categoryId: row.categoryId,
      entryId: cell.entryId,
      values,
    })
  }

  return (
    <div className="space-y-4">
      <TimesheetToolbar
        nav={<WeekNav weekId={weekId} onChange={(week) => onNavigate(week, dayIndex)} />}
        readout={
          // Deze lijst draait alleen onder md, dus vrijwel altijd op een telefoon. Daar
          // lezen de staafjes van 12px zonder label als vegen in plaats van als grafiek
          // en zijn ze nauwelijks te tikken; de weekstepper doet daar het werk.
          weekTotalsQuery.data?.weeks?.length ? (
            <div className="hidden sm:block">
              <WeekTotalsStrip totals={weekTotalsQuery.data.weeks} weekId={weekId} onChange={(week) => onNavigate(week, dayIndex)} />
            </div>
          ) : null
        }
        actions={
          week && !stale ? (
            <WeekApprovalControl
              approval={week.approval}
              weekId={weekId}
              personId={personId}
              editable={editable}
              weekLabel={meta.label}
              emptyWorkdays={emptyWorkdays}
            />
          ) : null
        }
      />

      {week?.utilization ? (
        <UtilizationStrip
          personId={personId}
          weekId={weekId}
          utilization={week.utilization}
          dailyTotals={week.dailyTotals}
        />
      ) : null}

      <div className="rounded-card border border-hairline bg-surface p-2 shadow-card">
        <div className="flex items-center justify-between gap-2 px-1.5 pb-2">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-ink">
              {day.label.charAt(0).toUpperCase() + day.label.slice(1)}
            </span>
            {dayInfo?.dayLabel ? (
              <span className={cn('shrink-0 rounded-pill px-2 py-0.5 text-xs font-medium', dayInfo.dayType === 'vacation' ? 'bg-fill-2 text-ink-soft' : 'bg-accent-600/10 text-accent-700')}>
                {dayInfo.dayLabel}
              </span>
            ) : null}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={goToToday}
              disabled={todayIso === day.iso}
              title={todayIso === day.iso ? 'Je zit al op vandaag' : undefined}
              className="btn-ghost h-8 px-2.5 text-sm"
            >
              Vandaag
            </button>
            {canEdit && (dayInfo ? dayInfo.dayType !== 'weekend' : day.isWorkday) ? (
              <DayHeaderPopover
                weekId={weekId}
                personId={personId}
                date={day.iso}
                short={day.short}
                dayNumber={day.date.getDate()}
                isToday={false}
                dayType={dayInfo?.dayType ?? (day.isWorkday ? 'workday' : 'weekend')}
                dayLabel={dayInfo?.dayLabel ?? null}
                exception={exception}
                editable={canEdit}
              >
                <span className="btn-ghost h-8 w-8 justify-center p-0" aria-label="Dagacties">
                  <CalendarCog className="h-4 w-4" />
                </span>
              </DayHeaderPopover>
            ) : null}
          </span>
        </div>

        <div className="flex gap-1">
          {meta.days.map((d) => {
            const info = week?.dailyTotals.find((t) => t.dayIndex === d.dayIndex)
            const total = toNumber(info?.total)
            const selected = d.dayIndex === dayIndex
            const isToday = d.iso === todayIso
            return (
              <button
                key={d.dayIndex}
                type="button"
                onClick={() => onNavigate(weekId, d.dayIndex)}
                aria-pressed={selected}
                aria-label={`${d.label}, ${total > 0 ? `${formatHmm(total)} uur` : 'leeg'}`}
                className={cn(
                  'flex flex-1 flex-col items-center gap-1 rounded-lg py-1.5 transition-colors',
                  selected ? 'bg-accent-600/10 text-accent-700' : d.isWorkday ? 'text-ink-soft' : 'text-ink-faint',
                  !selected && isToday && 'ring-1 ring-accent-600/35',
                )}
              >
                <span className="text-[10px] font-medium uppercase tracking-wide">{d.short}</span>
                <span className={cn('num text-sm', selected && 'font-semibold')}>{d.date.getDate()}</span>
                <StoplichtDot status={dayStatus(total, info?.expectedWorkday ?? d.isWorkday)} />
              </button>
            )
          })}
        </div>
      </div>

      {freeDay ? (
        <FreeDayBanner
          weekId={weekId}
          personId={personId}
          dayIso={day.iso}
          dayInfo={dayInfo}
          exception={exception}
          editable={canEdit}
        />
      ) : null}

      <div className="space-y-2" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {weekQuery.isLoading ? (
          <>
            <Skeleton className="h-24 w-full rounded-card" />
            <Skeleton className="h-24 w-full rounded-card" />
          </>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="Nog geen uren op deze dag"
            description="Kies waar je aan werkte."
            className="px-4 py-10"
          />
        ) : (
          rows.map((row, rowIdx) => {
            const pickerData = pickersQuery.data
            // Net als verwijderen werkt de wissel op de hele weekregel, ook al toont
            // deze weergave maar een dag.
            const menu = rowMenuState(row, canEdit, pickerData, savedRowKeys.has(row.rowKey))
            const boundary = rowBoundary(row, rows[rowIdx - 1])
            const firstOfClient = boundary.firstOfClient
            // Het rij-label zit in de tapzone van de kaart, dus de menu's staan als
            // icoonknoppen in de actiestrip eronder.
            const menuSlot = (
              <>
                {[
                  { level: 'client' as const, allowed: menu.canChangeClient && firstOfClient, icon: Building2, label: `Klant wijzigen: ${row.clientName}` },
                  { level: 'project' as const, allowed: menu.canChangeProject, icon: FolderInput, label: `Project wijzigen: ${row.projectName}` },
                  { level: 'category' as const, allowed: menu.canChangeCategory, icon: Tag, label: `Categorie wijzigen: ${row.categoryName ?? 'geen categorie'}` },
                ].map(({ level, allowed, icon: Icon, label }) =>
                  allowed && pickerData ? (
                    <RowTargetMenu
                      key={level}
                      row={row}
                      pickers={pickerData}
                      level={level}
                      entryIds={menu.entryIds}
                      weekId={weekId}
                      personId={personId}
                      onLocalCommit={menu.isLocalRow ? (selection) => replaceRow(row.rowKey, selection, dayIndex) : undefined}
                      trigger={
                        <button type="button" aria-label={label} title={label} className={cardActionClass}>
                          <Icon className="h-4 w-4" />
                        </button>
                      }
                    />
                  ) : null,
                )}
              </>
            )
            return (
              <DayCard
                key={`${row.rowKey}:${day.iso}`}
                row={row}
                cell={row.cells[dayIndex]}
                rowIdx={rowIdx}
                weekId={weekId}
                personId={personId}
                {...boundary}
                landed={row.rowKey === landedRowKey}
                editable={canEdit}
                saved={savedRowKeys.has(row.rowKey)}
                otherHoursOnDay={dayTotal - toNumber(row.cells[dayIndex].hours)}
                menuSlot={menuSlot}
                onCopyRow={() => addRowOnDay(selectionFromRow(row))}
                onAddRow={addRowOnDay}
                pickers={pickerData}
                onDropLocalRow={() => dropRow(row.rowKey)}
                onSave={(values) => saveCellValues(row, values)}
              />
            )
          })
        )}
      </div>

      {canEdit && pickersQuery.data ? (
        <AddRowButton
          pickers={pickersQuery.data}
          onAdd={addRowOnDay}
        />
      ) : null}
      {editable && (weekClosed || dayIsClosed) ? (
        <p className="px-1 text-sm text-ink-faint">
          {weekClosed
            ? 'Week is afgesloten - heropen de week om uren te wijzigen.'
            : 'Deze dag is met de maand afgesloten - heropen de week om de uren te wijzigen.'}
        </p>
      ) : null}

      <div className="flex items-center justify-between rounded-card bg-fill-1 px-4 py-3 text-sm">
        <span className="text-ink-soft">Dagtotaal</span>
        <span className="num font-semibold text-ink">{formatHmm(dayTotal)}</span>
      </div>

      <Legend />
    </div>
  )
}

const cardActionClass =
  'rounded-sm p-1 text-ink-faint transition-colors duration-150 hover:bg-fill-2 hover:text-accent-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40'

interface DayCardProps {
  row: WeekRow
  cell: WeekCell
  rowIdx: number
  weekId: string
  personId?: string
  firstOfClient: boolean
  firstOfProject: boolean
  isContinuation: boolean
  landed: boolean
  editable: boolean
  /** Er is voor deze regel al weggeschreven, ook al zijn de entry-ids nog niet binnen. */
  saved: boolean
  otherHoursOnDay: number
  /** Project- en categoriemenu, gerenderd in de actiestrip naast de rij-acties. */
  menuSlot?: ReactNode
  onCopyRow: () => void
  onAddRow: (selection: NewRowSelection) => void
  pickers: PickerData | undefined
  onDropLocalRow: () => void
  onSave: (values: CellValues) => void
}

/**
 * De mobiele tegenhanger van een rij uit de dagweergave: de kaart leest af wat daar in
 * kolommen staat (uren, omschrijving, reiskosten) en tappen opent dezelfde popover om het
 * te wijzigen. De rij-acties staan op een eigen regel, want een knop mag niet in de
 * tapzone van de kaart genest zitten.
 */
function DayCard({
  row,
  cell,
  rowIdx,
  weekId,
  personId,
  firstOfClient,
  isContinuation,
  landed,
  editable,
  saved,
  otherHoursOnDay,
  menuSlot,
  onCopyRow,
  onAddRow,
  pickers,
  onDropLocalRow,
  onSave,
}: DayCardProps) {
  const hours = toNumber(cell.hours)
  const travelAmount = toNumber(cell.travelAmount)
  const hasTravel = travelAmount > 0
  const description = readDescription(cell.description, hours)
  const tappable = editable && !cell.locked

  const body = (
    <span className="flex w-full flex-col gap-1.5 px-4 py-3 text-left">
      <span className="flex items-start justify-between gap-3">
        <RowLabel row={row} firstOfClient={firstOfClient} isContinuation={isContinuation} stacked />
        <span className="flex shrink-0 items-center gap-1.5">
          {cell.locked ? <Lock className="h-3 w-3 text-ink-faint" aria-hidden="true" /> : null}
          <span className={cn('num text-base', hours > 0 ? 'font-medium text-ink' : 'text-ink-faint')}>
            {hours > 0 ? formatHmm(hours) : '0:00'}
          </span>
        </span>
      </span>
      <span className="flex items-end justify-between gap-3">
        <span className={cn('min-w-0 flex-1 text-sm', description.missing ? 'text-ink-faint/70' : 'text-ink-soft')}>
          {description.text}
        </span>
        {hasTravel ? (
          <span className="money shrink-0 rounded-pill bg-fill-2 px-2 py-0.5 text-xs font-medium text-ink-soft">
            {formatEuros(travelAmount)}
          </span>
        ) : tappable ? (
          <span className="shrink-0 text-xs font-medium text-ink-faint">+ €</span>
        ) : null}
      </span>
    </span>
  )

  const trigger = tappable ? (
    <button
      type="button"
      id={`daylist-${rowIdx}`}
      aria-label={[
        `${row.clientName} ${row.projectName}`,
        hours > 0 ? formatHmm(hours) : 'geen uren',
        cell.description || 'geen omschrijving',
        hasTravel ? `reiskosten ${formatEuros(travelAmount)}` : 'geen reiskosten',
      ].join(', ')}
      className="w-full transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500/40 active:bg-accent-600/5"
    >
      {body}
    </button>
  ) : (
    <Tooltip content={cell.locked ? (cell.invoiceNumber ? `Gefactureerd op ${cell.invoiceNumber}` : 'Gefactureerd') : null}>
      <span className="block w-full">{body}</span>
    </Tooltip>
  )

  return (
    <div
      className={cn(
        'overflow-hidden rounded-card border border-hairline bg-surface shadow-card transition-colors duration-700 ease-spring',
        // Extra lucht boven een nieuw klantblok: de kaarten hebben al een rand, dus een
        // hairline erbij zou alleen ruis zijn.
        firstOfClient && rowIdx > 0 && 'mt-2',
        landed && 'bg-accent-600/6',
      )}
    >
      <CellPopover
        row={row}
        cell={cell}
        editable={editable}
        cellId={`daylist-${rowIdx}`}
        otherHoursOnDay={otherHoursOnDay}
        onSave={onSave}
        trigger={trigger}
      />
      {editable ? (
        <div className="flex items-center justify-end gap-0.5 border-t border-hairline px-2 py-1">
          {menuSlot}
          <RowActions
            row={row}
            weekId={weekId}
            personId={personId}
            saved={saved}
            dayIndex={cell.dayIndex}
            onCopyRow={onCopyRow}
            onAddRow={onAddRow}
            pickers={pickers}
            onDropLocalRow={onDropLocalRow}
          />
        </div>
      ) : null}
    </div>
  )
}
